import { t } from 'i18next';
import type { JSX } from 'react';
import React, { useState } from 'react';

import { CardV2 as Card, Chip, Collapse, SegmentedControl, Well } from '@feathr/components';

import * as styles from './FacebookCreativeSpecifications.css';

function FacebookCreativeSpecifications(): JSX.Element {
  const [activeTab, setActiveTab] = useState<'images' | 'videos'>('images');

  const aspectRatioWarning = t(
    'Resolutions that are not 9:16 may show black bars above and below the image',
  );
  const belowMinimumResolution = t('Below recommended resolution');
  const recommendedResolution = t('Recommended by Meta');
  const recommendedResolutionMobile = t('Recommended by Meta for mobile');
  const recommendedResolutionDesktop = t('Recommended by Meta for desktop');
  const dynamicAds = t('Dynamic ads only use this in stories');
  const offerAds = t('Offer ads and ads using stock photos only support this ratio');
  const maskedDown = t('May be masked down to 1:1');

  function handleTabChange(tab: string): void {
    setActiveTab(tab as 'images' | 'videos');
  }

  const data = [
    { label: t('Images'), value: 'images' },
    { label: t('Videos'), value: 'videos' },
  ];

  return (
    <Card.Content>
      <Collapse title={t('Creative specifications')}>
        <SegmentedControl data={data} onChange={handleTabChange} value={activeTab} />

        {activeTab === 'images' && (
          <div className={styles.root}>
            <Well layout={'vertical'} theme={'white'}>
              <Well.Row description={'JPEG or PNG'} label={t('File types')} />
              <Well.Row description={'20 MB'} label={t('Maximum file size')} />
            </Well>

            <table className={styles.specTable}>
              <thead>
                <tr>
                  <th>{t('Platform')}</th>
                  <th>{t('Ratio')}</th>
                  <th>{t('Minimum Resolution')}</th>
                  <th>{t('Recommended Resolution')}</th>
                </tr>
              </thead>
              <tbody>
                {/* Facebook Stories, Instagram Stories and Instagram Reels */}
                <tr>
                  <td rowSpan={5}>
                    {t('Facebook Stories, Instagram Stories and Instagram Reels')}
                  </td>
                  <td>1:1</td>
                  <td>
                    <Chip tooltip={aspectRatioWarning}>1080 x 1080</Chip>
                  </td>
                  <td>
                    <Chip tooltip={aspectRatioWarning}>1440 x 1440</Chip>
                  </td>
                </tr>
                <tr>
                  <td>4:5</td>
                  <td>
                    <Chip tooltip={aspectRatioWarning}>1080 x 1350</Chip>
                  </td>
                  <td>
                    <Chip tooltip={aspectRatioWarning}>1440 x 1800</Chip>
                  </td>
                </tr>
                <tr>
                  <td>9:16</td>
                  <td>
                    <Chip tooltip={belowMinimumResolution}>1080 x 1920</Chip>
                  </td>
                  <td>
                    <Chip theme={'violet'} tooltip={recommendedResolution}>
                      1440 x 2560
                    </Chip>
                  </td>
                </tr>
                <tr>
                  <td>16:9</td>
                  <td>
                    <Chip tooltip={aspectRatioWarning}>1920 x 1080</Chip>
                  </td>
                  <td>
                    <Chip tooltip={aspectRatioWarning}>2560 x 1440</Chip>
                  </td>
                </tr>
                <tr>
                  <td>1.91:1</td>
                  <td>
                    <Chip tooltip={dynamicAds}>1200 x 628</Chip>
                  </td>
                  <td>
                    <Chip tooltip={dynamicAds}>1200 x 628</Chip>
                  </td>
                </tr>
                {/* Facebook Reels */}
                <tr>
                  <td>{t('Facebook Reels')}</td>
                  <td>9:16</td>
                  <td>
                    <Chip tooltip={belowMinimumResolution}>1080 x 1920</Chip>
                  </td>
                  <td>
                    <Chip theme={'violet'} tooltip={recommendedResolution}>
                      1440 x 2560
                    </Chip>
                  </td>
                </tr>

                {/* Facebook Feed */}
                <tr>
                  <td rowSpan={4}>{t('Facebook Feed')}</td>
                  <td>1:1</td>
                  <td>
                    <Chip tooltip={belowMinimumResolution}>1080 x 1080</Chip>
                  </td>
                  <td>
                    <Chip theme={'violet'} tooltip={recommendedResolutionDesktop}>
                      1440 x 1440
                    </Chip>
                  </td>
                </tr>
                <tr>
                  <td>4:5</td>
                  <td>
                    <Chip tooltip={belowMinimumResolution}>1080 x 1350</Chip>
                  </td>
                  <td>
                    <Chip theme={'violet'} tooltip={recommendedResolutionMobile}>
                      1440 x 1800
                    </Chip>
                  </td>
                </tr>
                <tr>
                  <td>16:9</td>
                  <td>
                    <Chip tooltip={belowMinimumResolution}>1920 x 1080</Chip>
                  </td>
                  <td>
                    <Chip>2560 x 1440</Chip>
                  </td>
                </tr>
                <tr>
                  <td>1.91:1</td>
                  <td>
                    <Chip tooltip={offerAds}>1200 x 628</Chip>
                  </td>
                  <td>
                    <Chip tooltip={offerAds}>1200 x 628</Chip>
                  </td>
                </tr>

                {/* Instagram Feed */}
                <tr>
                  <td rowSpan={5}>{t('Instagram Feed')}</td>
                  <td>1:1</td>
                  <td>
                    <Chip tooltip={belowMinimumResolution}>1080 x 1080</Chip>
                  </td>
                  <td>
                    <Chip theme={'violet'} tooltip={recommendedResolutionDesktop}>
                      1440 x 1440
                    </Chip>
                  </td>
                </tr>
                <tr>
                  <td>4:5</td>
                  <td>
                    <Chip tooltip={belowMinimumResolution}>1080 x 1350</Chip>
                  </td>
                  <td>
                    <Chip theme={'violet'} tooltip={recommendedResolutionMobile}>
                      1440 x 1800
                    </Chip>
                  </td>
                </tr>
                <tr>
                  <td>9:16</td>
                  <td>
                    <Chip tooltip={maskedDown}>1080 x 1920</Chip>
                  </td>
                  <td>
                    <Chip tooltip={maskedDown}>1440 x 2560</Chip>
                  </td>
                </tr>
                <tr>
                  <td>16:9</td>
                  <td>
                    <Chip tooltip={maskedDown}>1920 x 1080</Chip>
                  </td>
                  <td>
                    <Chip tooltip={maskedDown}>2560 x 1440</Chip>
                  </td>
                </tr>
                <tr>
                  <td>1.91:1</td>
                  <td>
                    <Chip tooltip={maskedDown}>1200 x 628</Chip>
                  </td>
                  <td>
                    <Chip tooltip={maskedDown}>1200 x 628</Chip>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'videos' && (
          <div className={styles.root}>
            <Well layout={'vertical'} theme={'white'}>
              {/* TODO: add MOV back when we implement proper mov support */}
              <Well.Row description={'MP4 or GIF'} label={t('File types')} />
              <Well.Row description={'1 GB'} label={t('Maximum file size')} />
              <Well.Row description={t('H.264')} label={t('Compression')} />
              <Well.Row description={t('Square pixels')} label={t('Pixel aspect ratio')} />
              <Well.Row description={t('Fixed frame rate')} label={t('Frame rate type')} />
              <Well.Row description={t('Progressive scan')} label={t('Scan type')} />
              <Well.Row description={t('5 seconds to 2 minutes')} label={t('Duration')} />
              <Well.Row
                description={'Stereo AAC compression at 128kbps+'}
                label={t('Audio configuration')}
              />
              <Well.Row description={t('Optional, but recommended')} label={t('Sound')} />
              <Well.Row description={t('Optional, but recommended')} label={t('Captions')} />
            </Well>
            <table className={styles.specTable}>
              <thead>
                <tr>
                  <th>{t('Platform')}</th>
                  <th>{t('Ratio')}</th>
                  <th>{t('Minimum Resolution')}</th>
                  <th>{t('Recommended Resolution')}</th>
                </tr>
              </thead>
              <tbody>
                {/* Facebook and Instagram Stories/Reels */}
                <tr>
                  <td rowSpan={5}>{t('Facebook and Instagram Stories/Reels')}</td>
                  <td>1:1</td>
                  <td>
                    <Chip tooltip={aspectRatioWarning}>1080 x 1080</Chip>
                  </td>
                  <td>
                    <Chip tooltip={aspectRatioWarning}>1440 x 1440</Chip>
                  </td>
                </tr>
                <tr>
                  <td>4:5</td>
                  <td>
                    <Chip tooltip={aspectRatioWarning}>1080 x 1350</Chip>
                  </td>
                  <td>
                    <Chip tooltip={aspectRatioWarning}>1440 x 1800</Chip>
                  </td>
                </tr>
                <tr>
                  <td>9:16</td>
                  <td>
                    <Chip tooltip={belowMinimumResolution}>1080 x 1920</Chip>
                  </td>
                  <td>
                    <Chip theme={'violet'} tooltip={recommendedResolution}>
                      1440 x 2560
                    </Chip>
                  </td>
                </tr>
                <tr>
                  <td>16:9</td>
                  <td>
                    <Chip tooltip={aspectRatioWarning}>1920 x 1080</Chip>
                  </td>
                  <td>
                    <Chip tooltip={aspectRatioWarning}>2560 x 1440</Chip>
                  </td>
                </tr>
                <tr>
                  <td>1.91:1</td>
                  <td>
                    <Chip tooltip={dynamicAds}>1200 x 628</Chip>
                  </td>
                  <td>
                    <Chip tooltip={dynamicAds}>1200 x 628</Chip>
                  </td>
                </tr>
                {/* Facebook and Instagram Feed */}
                <tr>
                  <td rowSpan={5}>{t('Facebook and Instagram Feed')}</td>
                  <td>1:1</td>
                  <td>
                    <Chip tooltip={belowMinimumResolution}>1080 x 1080</Chip>
                  </td>
                  <td>
                    <Chip theme={'violet'} tooltip={recommendedResolutionDesktop}>
                      1440 x 1440
                    </Chip>
                  </td>
                </tr>
                <tr>
                  <td>4:5</td>
                  <td>
                    <Chip tooltip={belowMinimumResolution}>1080 x 1350</Chip>
                  </td>
                  <td>
                    <Chip theme={'violet'} tooltip={recommendedResolutionMobile}>
                      1440 x 1800
                    </Chip>
                  </td>
                </tr>
                <tr>
                  <td>9:16</td>
                  <td>
                    <Chip tooltip={maskedDown}>1080 x 1920</Chip>
                  </td>
                  <td>
                    <Chip tooltip={maskedDown}>1440 x 2560</Chip>
                  </td>
                </tr>
                <tr>
                  <td>16:9</td>
                  <td>
                    <Chip tooltip={maskedDown}>1920 x 1080</Chip>
                  </td>
                  <td>
                    <Chip tooltip={maskedDown}>2560 x 1440</Chip>
                  </td>
                </tr>
                <tr>
                  <td>1.91:1</td>
                  <td>
                    <Chip tooltip={maskedDown}>1200 x 628</Chip>
                  </td>
                  <td>
                    <Chip tooltip={maskedDown}>1200 x 628</Chip>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        <dl className={styles.instructions}>
          <dt>{t('Facebook & Instagram Stories')}</dt>
          <dd>
            {t(
              'Consider leaving roughly 14% (250 pixels) of the top and 20% (340 pixels) of the bottom of the video free from text and logos to avoid covering these elements with UI elements such as the call-to-action.',
            )}
          </dd>
          <dt>{t('Facebook & Instagram Reels')}</dt>
          <dd>
            {t(
              'Consider leaving roughly 14% of the top, 35% of the bottom, and 6% on each side of your asset free from text, logos, or other key creative elements to avoid cropping key elements or covering them with the profile icon or call-to-action.',
            )}
          </dd>
        </dl>
      </Collapse>
    </Card.Content>
  );
}

export default FacebookCreativeSpecifications;
